import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { CrystalVisual } from "@/components/CrystalVisual";
import { getRecentCrystals } from "@/data/api";
import { RITZYRA_APPROACH } from "@/data/crystalsData";

const SECTIONS = [
  { key: "library", emoji: "🔮", title: "Crystal Library", sub: "Search and explore individual crystals.", href: "/crystal-school/library" },
  { key: "basics", emoji: "🪨", title: "Crystal Basics", sub: "The geology and terminology behind crystals.", href: "/crystal-school/basics" },
  { key: "wrong", emoji: "👀", title: "Things People Get Wrong", sub: "Common myths, confusing names and misconceptions.", href: "/crystal-school/wrong" },
  { key: "intentions", emoji: "✨", title: "Browse by Intention", sub: "Crystals commonly linked with spiritual themes.", href: "/crystal-school/intentions" },
  { key: "folklore", emoji: "📖", title: "Folklore & History", sub: "Historical beliefs and crystal folklore.", href: "/crystal-school/library?has=folklore&title=Folklore%20%26%20History" },
];

export default function Crystals() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    setRecent(getRecentCrystals());
  }, []);

  const submitSearch = () => {
    const q = query.trim();
    navigate(`/crystal-school/library${q ? `?search=${encodeURIComponent(q)}` : ""}`);
  };

  const sectionCard = (s, fav) => (
    <button key={s.key} data-testid={`section-${s.key}`} className="pressable" onClick={() => navigate(s.href)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, background: fav ? colors.brandTertiary : colors.surfaceSecondary, borderRadius: 18, border: `1px solid ${fav ? colors.brandTertiary : colors.border}`, padding: 16, marginBottom: 12, cursor: "pointer", textAlign: "left" }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: colors.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 22 }}>
        {fav ? <Icon name="heart" size={22} color={colors.brand} /> : s.emoji}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: colors.onSurface, fontSize: 17, fontWeight: 700, fontFamily: fonts.text }}>{s.title}</div>
        <div style={{ color: colors.muted, fontSize: 13, marginTop: 2, lineHeight: "18px", fontFamily: fonts.text }}>{s.sub}</div>
      </div>
      <Icon name="chevron-right" size={22} color={colors.muted} />
    </button>
  );

  return (
    <div className="ritzy-fade">
      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${colors.divider}` }}>
        <div style={{ color: colors.onSurface, fontSize: 28, fontFamily: fonts.display, fontWeight: 700 }}>Crystal School 🔮</div>
        <div style={{ color: colors.onSurface, fontSize: 16, fontWeight: 700, marginTop: 6, fontFamily: fonts.text }}>Your no-bullshit guide to crystals ✨</div>
        <div style={{ color: colors.muted, fontSize: 13, marginTop: 4, lineHeight: "19px", fontFamily: fonts.text }}>Geology, spiritual associations, folklore and the stuff people get wrong — everything you need to talk about crystals with confidence.</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 14, padding: "0 14px", height: 48 }}>
          <Icon name="search" size={18} color={colors.muted} />
          <input data-testid="crystal-search-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitSearch()} placeholder="Search crystals..." style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: colors.onSurface, fontSize: 15, fontFamily: fonts.text }} />
          {query ? <button onClick={() => setQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}><Icon name="x" size={18} color={colors.muted} /></button> : null}
        </div>
      </div>

      <div style={{ padding: "18px 24px 32px" }}>
        {SECTIONS.map((s) => sectionCard(s, false))}
        {sectionCard({ key: "favourites", title: "My Crystals ✨", sub: "Crystals you've saved to come back to.", href: "/crystal-school/favourites" }, true)}

        {recent.length ? (
          <>
            <div style={{ color: colors.onSurface, fontSize: 18, fontFamily: fonts.display, fontWeight: 700, margin: "10px 0 12px" }}>Recently Viewed</div>
            <div className="hide-scroll" style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
              {recent.map((r) => (
                <button key={r.slug} data-testid={`recent-${r.slug}`} className="pressable" onClick={() => navigate(`/crystal/${r.slug}`)} style={{ width: 120, flexShrink: 0, border: "none", background: "none", padding: 0, cursor: "pointer" }}>
                  <CrystalVisual name={r.name} seed={r.visual_seed} height={96} compact />
                </button>
              ))}
            </div>
          </>
        ) : null}

        <div data-testid="ritzyra-approach" style={{ marginTop: 18, background: colors.brandTertiary, borderRadius: 18, padding: 18 }}>
          <div style={{ color: colors.onBrandTertiary, fontSize: 17, fontFamily: fonts.display, fontWeight: 700 }}>{RITZYRA_APPROACH.title}</div>
          {RITZYRA_APPROACH.lines.map((l, i) => <div key={i} style={{ color: colors.onBrandTertiary, fontSize: 13, lineHeight: "20px", marginTop: 8, fontFamily: fonts.text }}>{l}</div>)}
        </div>

        {user?.is_admin ? (
          <button data-testid="admin-crystals-entry" className="pressable" onClick={() => navigate("/admin/crystals")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 14, padding: "14px", marginTop: 18, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: fonts.text }}>
            <Icon name="settings" size={16} color={colors.onBrand} />Manage Crystal School (Admin)
          </button>
        ) : null}
      </div>
    </div>
  );
}
